// /resources/js/store/CartStore.js

import {defineStore} from 'pinia';
import {getLocalStorage, setLocalStorage, checkDocument, scrollTop} from '@js/helpers.ts';
import {useThemeStore} from "@js/store/ThemeStore.js";
import {ecomAdd, ecomRemove, ecomPurchase, mapProduct} from '@js/ecommerce.js';

// ----- Имена куки и срок хранения -----
const COOKIE_CART = 'cart_metal_v1';     // [{ id, qty }]
const COOKIE_UI = 'cart_ui_metal_v1';  // { [id]: { qty } }
const COOKIE_OPTS = 'cart_opts_metal_v1';// { [id]: { type, price_type, fiber } }
const YEAR_IN_MINUTES = 525600;          // 1 год в минутах

// ----- Вспомогательные округления -----
function roundMoney(v) {
    return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
}

function roundPieces(v) {
    return Math.max(1, Math.round(Number(v)));
}

function roundArea(v) {
    return Number(Number(v / 1000 || 0).toFixed(2)); // м² до тысячных
}

export const useCartStore = defineStore('cartStore', {
    state: () => ({
        // минимальный снэпшот продукта
        catalog: {},                 // { [id]: { id, title, slug, category_slug, price, available, images, weight, length, width } }

        // локальное состояние карточек: только количество (шт)
        ui: {},                      // { [id]: { qty } }

        // выбор спецификаций, сохраняем в cookie
        opts: {},                    // { [id]: { type:null|'GOST'|'TU', price_type:null|'price'|'price_05'|..., fiber:null|'fiber_polypropylene'|'fiber_steel' } }

        // корзина: только id + qty (шт)
        items: [],                   // [{ id, qty }]

        loadings: {
            cart_loading: true,
            sending: false,
        },

        isCartClearModalVisible: false,
        cart_products: [],           // данные товаров из /axios/products/get-by-ids (с ценой уже пересчитанной под опции)

        form: {
            full_name: '',
            phone: '',
            email: '',
            inn: '',
            comment: '',
            captcha_token: '',

            delivery_type: 'pickup',
            delivery_address: '',

            vehicle_type: 'manipulator',
            vehicle: 'mixer',
            payment_type: 'cash',
        },
        cart_form_successfully_sent: false,
        form_errors: [],
    }),

    getters: {
        // продукт из каталога
        productOf: (s) => (id) => s.catalog[id] ?? null,

        // ui карточки (только qty)
        uiOf: (s) => (id) => s.ui[id] ?? {qty: 1},

        // шаг/минимум
        step() {
            return () => 1;
        },
        minQty() {
            return () => 1;
        },

        // активная цена: одна — price (берём из catalog снапшота)
        priceActive() {
            return (id) => {
                const p = this.productOf(id);
                if (!p) return 0;
                const sel = this.getSelection(id);
                const specs = Array.isArray(p.beton_price_specifications) ? p.beton_price_specifications : [];
                if (specs.length) {
                    return Number(this._computeBetonPrice(p, sel));  // вычисляем из спецификаций
                }
                return Number(p.price || 0);                       // иначе базовая цена
            };
        },

        // только шт
        unitLabel() {
            return () => 'шт';
        },

        // доступность
        isAvailable() {
            return (id) => !!this.productOf(id)?.available;
        },

        // поиск строки в корзине (без режима)
        findItem: (s) => (id) => s.items.find(i => i.id === Number(id)),

        // флаги наличия
        isInCartExact() {
            return (id) => !!this.findItem(id);
        },
        isInCartAny() {
            return (id) => !!this.findItem(id);
        },

        // сумма по позиции: price * qty
        totalPrice() {
            return (id) => roundMoney(this.priceActive(id) * Number(this.uiOf(id).qty || 0));
        },

        // сумма по корзине
        subTotal(state) {
            return roundMoney(state.items.reduce((sum, it) => {
                const price = Number(this.priceActive(it.id) || 0);
                return sum + price * Number(it.qty);
            }, 0));
        },

        // вес по позиции (если задан weight), иначе null
        weightKg() {
            return (id) => {
                const qty = roundPieces(this.uiOf(id).qty);
                const mpp = Number(this.productOf(id)?.weight ?? 0);
                if (!mpp) return null;
                return Number((qty * mpp).toFixed(3));
            };
        },

        // общий вес (если есть weight)
        weightTotal(state) {
            const sumKg = state.items.reduce((sum, it) => {
                const p = this.productOf(it.id);
                if (!p) return sum;
                const mpp = Number(p.weight || 0);
                const kg = roundPieces(it.qty) * mpp;
                return sum + (Number.isFinite(kg) ? kg : 0);
            }, 0);
            return Number(sumKg.toFixed(3));
        },

        // площадь одной штуки (м²) если есть L*W (в метрах)
        areaPerPiece() {
            return (id) => {
                const p = this.productOf(id);
                const L = Number(p?.length ?? 0);
                const W = Number(p?.width ?? 0);
                if (!L || !W) return null;
                return roundArea(L * W);
            };
        },

        areaSqM() {
            return (id) => {
                const perPiece = this.areaPerPiece(id);
                if (perPiece == null) return null;
                const qty = roundPieces(this.uiOf(id).qty);
                return roundArea(perPiece * qty);
            };
        },

        totalArea(state) {
            const sum = state.items.reduce((acc, it) => {
                const p = this.productOf(it.id);
                if (!p) return acc;
                const L = Number(p.length ?? 0);
                const W = Number(p.width ?? 0);
                if (!L || !W) return acc;
                const perPiece = L * W;
                return acc + perPiece * roundPieces(it.qty);
            }, 0);
            return roundArea(sum);
        },
    },

    actions: {
        // ===== helpers: e-commerce =====
        _mapForEcom(id, qty, list = 'Cart') {
            const p = this.productOf(id);
            if (!p) return null;
            const mp = mapProduct(p, {list, position: 1, qty: Number(qty || 1)});
            mp.variant = 'шт';
            return mp;
        },

        _trackCartDelta(id, prevQty, nextQty) {
            const p = this.productOf(id);
            if (!p || prevQty == null || nextQty == null) return;
            const diff = Number(nextQty) - Number(prevQty);
            if (diff === 0) return;
            const qtyAbs = Math.abs(diff);
            const mp = this._mapForEcom(id, qtyAbs, 'Cart');
            if (!mp) return;
            try {
                if (diff > 0) ecomAdd(mp);
                else ecomRemove(mp);
            } catch {
            }
        },

        // ===== INIT =====
        hydrate() {
            this.loadCart();
            this.loadUI();
            this.loadOpts();           // 👈 загрузим выборы спецификаций
            this._persistUIFiltered();
        },

        // Регистрируем продукт (только необходимые поля)
        // registerCatalogProduct(product)
        registerCatalogProduct(product) {
            const id = Number(product.id);

            // База всегда из "сырой" цены бэкенда (НЕ из текущей)
            const base = Number(product.price ?? 0);

            this.catalog[id] = {
                id: product.id,
                title: product.title,
                slug: product.slug,
                category_slug: product.category_slug,

                // ✳️ Храним ДВЕ цены:
                base_price: base,                                   // неизменяемая база
                price: Number(product.current_price ?? base),       // текущая (для UI)

                available: !!product.available,
                images: product.images ?? null,
                weight: product.weight ?? null,
                length: product.length ?? null,
                width: product.width ?? null,

                beton_price_specifications: this._normalizeSpecs(
                    product.beton_price_specifications ?? product.beton_prices ?? []
                ),
            };

            if (!this.ui[id]) this.ui[id] = {qty: 1};
        },

        // ===== UI (в куки только если товар в корзине) =====
        loadUI() {
            const raw = getLocalStorage(COOKIE_UI) || '{}';
            try {
                const obj = JSON.parse(raw);
                this.ui = (obj && typeof obj === 'object') ? obj : {};
            } catch {
                this.ui = {};
            }
        },

        _persistUIFiltered() {
            const tracked = new Set(this.items.map(i => i.id));
            const toSave = {};
            for (const [k, v] of Object.entries(this.ui)) {
                const id = Number(k);
                if (tracked.has(id)) toSave[k] = v;
            }
            setLocalStorage(COOKIE_UI, JSON.stringify(toSave), YEAR_IN_MINUTES);
        },

        _syncCartLineIfExists(id) {
            const ui = this.uiOf(id);
            const line = this.findItem(id);
            if (!line) return;
            const prev = line.qty;
            line.qty = roundPieces(ui.qty);
            this._trackCartDelta(id, prev, line.qty);
            if (line.qty <= 0) this.remove(id);
            this.persistCart();
            this._persistUIFiltered();
        },

        // ===== Количество (шт) =====
        incQty(id) {
            const ui = this.ui[id];
            if (!ui) return;
            const line = this.findItem(id);
            const prev = line ? line.qty : null;

            ui.qty = roundPieces(ui.qty + 1);
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id)?.qty ?? null;
                this._trackCartDelta(id, prev, next);
            }
        },

        decQty(id) {
            const ui = this.ui[id];
            if (!ui) return;
            const line = this.findItem(id);
            const prev = line ? line.qty : null;

            ui.qty = roundPieces(Math.max(1, ui.qty - 1));
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id)?.qty ?? null;
                this._trackCartDelta(id, prev, next);
            }
        },

        setQty(id, v) {
            const ui = this.ui[id];
            if (!ui) return;
            const num = Number(v);
            if (Number.isNaN(num)) return;

            const line = this.findItem(id);
            const prev = line ? line.qty : null;

            ui.qty = roundPieces(Math.max(1, num));
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id)?.qty ?? null;
                this._trackCartDelta(id, prev, next);
            }
        },

        setQtyFromInput(id, raw) {
            // допускаем запятую, но всё равно округляем до целого >= 1
            this.setQty(id, String(raw).replace(',', '.'));
        },

        // ===== CART =====
        loadCart() {
            const raw = getLocalStorage(COOKIE_CART) || '[]';
            try {
                const arr = JSON.parse(raw);
                this.items = Array.isArray(arr)
                    ? arr
                        .map(r => {
                            // миграция со старого формата {id, mode:'pc', qty}
                            const id = Number(r.id);
                            const qty = roundPieces(r.qty);
                            if (!id || !qty) return null;
                            return {id, qty};
                        })
                        .filter(Boolean)
                    : [];
            } catch {
                this.items = [];
            }
        },

        // === Опции спецификаций (ГОСТ/ТУ, ПМД, фибра) ===
        loadOpts() {
            const raw = getLocalStorage(COOKIE_OPTS) || '{}';
            try {
                this.opts = JSON.parse(raw) || {};
            } catch {
                this.opts = {};
            }
        },
        persistOpts() {
            setLocalStorage(COOKIE_OPTS, JSON.stringify(this.opts), YEAR_IN_MINUTES);
        },
        setSelection(id, part) {
            id = Number(id);
            const prev = this.opts[id] ?? {type: null, price_type: null, fiber: null};
            const next = {...prev, ...part};
            this.opts[id] = next;
            this.persistOpts();

            const p = this.productOf(id);
            if (p) {
                const newPrice = Number(this._computeBetonPrice(p, next));
                this.updateCatalogPrice(id, newPrice); // меняем только current price
            }

            const idx = this.cart_products.findIndex(x => Number(x.id) === id);
            if (idx >= 0) {
                this.cart_products[idx].price = Number(this.priceActive(id));
            }
        },
        getSelection(id) {
            return this.opts[Number(id)] ?? {type: null, price_type: null, fiber: null};
        },

        // === Пересчёт цены под выбор спецификаций ===
        _computeBetonPrice(product, sel = {}) {
            const base = Number(product?.base_price ?? product?.price ?? 0); // ← вот так

            const isEmptySel =
                !sel ||
                (sel.type == null && sel.price_type == null && (sel.fiber == null || sel.fiber === false));

            if (isEmptySel) return base;

            const specs = Array.isArray(product?.beton_price_specifications)
                ? product.beton_price_specifications
                : [];
            if (!specs.length) return base;

            const byType = specs.filter(s => !sel.type || String(s.type).toUpperCase() === String(sel.type).toUpperCase());
            const byFiber = byType.filter(s => {
                if (!sel.fiber) return true;
                const v = s[sel.fiber];
                return v === true || v === 1 || Number(v) === 1;
            });

            const list = byFiber.length ? byFiber : byType;
            if (!list.length) return base;

            const field = sel.price_type ?? 'price';
            const nums = list
                .map(s => Number(s?.[field]))
                .filter(n => Number.isFinite(n) && n > 0);

            return nums.length ? Math.min(...nums) : base;
        },


        // === загрузка данных корзины ===
        getCartData() {
            this.loadings.cart_loading = true;
            axios.post('/axios/products/get-by-ids', {
                ids: this.items.map(i => i.id) ?? [],
                with_specs: true,
            })
                .then(response => {
                    const arr = response?.data?.products ?? [];

                    for (const p of arr) {
                        // ✳️ Зафиксируем базовую цену, если ещё не зафиксирована
                        if (typeof p.base_price !== 'number') {
                            p.base_price = Number(p.price ?? 0);
                        }

                        const sel = this.getSelection(p.id);
                        const nextPrice = this._computeBetonPrice(p, sel);

                        // Для шаблонов cart_products по-прежнему используем p.price как «текущую»
                        p.price = Number(nextPrice);

                        // Синхронизируем снапшот каталога:
                        // - base_price не трогаем
                        // - текущую цену обновляем
                        const snap = this.productOf(p.id);
                        if (snap) {
                            if (typeof snap.base_price !== 'number') {
                                snap.base_price = Number(snap.base_price ?? p.base_price ?? 0);
                            }
                            this.updateCatalogPrice(p.id, p.price);
                        }
                    }

                    this.cart_products = arr;
                    this.loadings.cart_loading = false;
                })
                .catch(() => {
                    this.loadings.cart_loading = false;
                });
        },

        persistCart() {
            setLocalStorage(COOKIE_CART, JSON.stringify(this.items), YEAR_IN_MINUTES);
        },

        canAddToCart(id) {
            return this.isAvailable(id) && this.priceActive(id) > 0;
        },

        addToCartFromCard(id) {
            id = Number(id);
            if (!this.canAddToCart(id)) return;

            const normalizedQty = roundPieces(this.uiOf(id).qty);
            const existing = this.findItem(id);

            if (existing) {
                const prev = existing.qty;
                existing.qty = roundPieces(existing.qty + normalizedQty);
                const mp = this._mapForEcom(id, (existing.qty - prev), 'Cart');
                if (mp) {
                    try {
                        ecomAdd(mp);
                    } catch {
                    }
                }
            } else {
                this.items.push({id, qty: normalizedQty});
                const mp = this._mapForEcom(id, normalizedQty, 'Cart');
                if (mp) {
                    try {
                        ecomAdd(mp);
                    } catch {
                    }
                }
            }

            this.persistCart();
            this._persistUIFiltered();

            const themeStore = useThemeStore();
            themeStore.addToast("Добавлено в корзину", "success", {route_name: 'cart'}, 5000);
        },

        // Сигнатура оставлена прежней: (id, mode, qty) — mode игнорируется
        updateItemQty(id, _mode, qty) {
            const it = this.findItem(Number(id));
            if (!it) return;
            const prev = it.qty;
            it.qty = roundPieces(qty);
            if (it.qty <= 0) {
                const mp = this._mapForEcom(Number(id), prev, 'Cart');
                if (mp) {
                    try {
                        ecomRemove(mp);
                    } catch {
                    }
                }
                this.remove(id);
                return;
            }
            this._trackCartDelta(Number(id), prev, it.qty);
            this.persistCart();
            this._persistUIFiltered();
        },

        // Сигнатура оставлена прежней: (id, mode) — mode игнорируется
        remove(id, _mode) {
            const it = this.findItem(Number(id));
            if (it) {
                const mp = this._mapForEcom(Number(id), it.qty, 'Cart');
                if (mp) {
                    try {
                        ecomRemove(mp);
                    } catch {
                    }
                }
            }
            this.items = this.items.filter(i => i.id !== Number(id));
            this.persistCart();

            // чистим ui, если товара больше нет
            const stillHas = this.items.some(i => i.id === Number(id));
            if (!stillHas) {
                delete this.ui[id];
                delete this.opts[id]; // заодно очищаем выбор спецификаций
                this.persistOpts();
            }

            this._persistUIFiltered();
            this.getCartData();
        },

        clear() {
            try {
                for (const it of this.items) {
                    const mp = this._mapForEcom(it.id, it.qty, 'Cart');
                    if (mp) ecomRemove(mp);
                }
            } catch {
            }

            this.items = [];
            this.persistCart();

            // очищаем ui и опции
            this.ui = {};
            this._persistUIFiltered();

            this.opts = {};
            this.persistOpts();

            this.getCartData();
        },

        // Позволяет извне обновить цену в снапшоте каталога
        updateCatalogPrice(id, newPrice) {
            id = Number(id);
            if (!this.catalog[id]) return;
            this.catalog[id].price = Number(newPrice || 0);   // ← НЕ трогаем base_price
        },

        // Отправка формы: отправляем и выбор спецификаций тоже
        sendForm() {
            if (!checkDocument()) return;

            this.form_errors = [];
            this.loadings.sending = false;
            this.cart_form_successfully_sent = false;
            this.form.captcha_token = '';

            const captchaId = 'smart-captcha-container-cart-form';

            const existing = document.getElementById(captchaId);
            if (existing) {
                try {
                    window.smartCaptcha.destroy(existing.dataset.widgetId);
                } catch (e) {
                }
                existing.remove();
            }

            const container = document.createElement('div');
            container.id = captchaId;
            container.style.position = 'absolute';
            container.style.zIndex = '-1';
            container.style.left = '-9999px';
            container.style.top = '-9999px';
            document.body.appendChild(container);

            let tokenReceived = false;

            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true;
                    this.loadings.sending = true;
                    this.form.captcha_token = token;

                    // добавляем opts к каждому item
                    const payloadItems = this.items.map(it => ({
                        id: it.id,
                        qty: it.qty,
                        opts: this.getSelection(it.id),
                    }));

                    axios.post('/axios/form/send-lead', {
                        bitrix_title: 'Оформление заказа на сайте',
                        ...this.form,
                        cart_products: payloadItems,
                    }).then(response => {
                        scrollTop();
                        this.loadings.sending = false;
                        this.cart_form_successfully_sent = true;

                        // === e-commerce: purchase ===
                        try {
                            const orderId = response?.data?.order_id ?? null;
                            const revenue = typeof response?.data?.revenue === 'number' ? response.data.revenue : undefined;
                            if (orderId) {
                                const products = this.items.map((it, idx) => {
                                    const p = this.productOf(it.id);
                                    const mp = mapProduct(p, {
                                        list: 'Purchase',
                                        position: idx + 1,
                                        qty: Number(it.qty || 1)
                                    });
                                    mp.variant = 'шт';
                                    return mp;
                                });
                                ecomPurchase({orderId, revenue, products});
                            }
                        } catch {
                        }

                        this.clear();

                        try {
                            window.smartCaptcha.destroy(widgetId);
                        } catch (e) {
                        }
                    }).catch(error => {
                        this.loadings.sending = false;
                        if (error.response?.status === 422) {
                            this.form_errors = error.response.data.errors;
                        }
                    });
                }
            });

            document.getElementById(captchaId).dataset.widgetId = widgetId;

            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.loadings.sending = false;
                    this.form_errors = {captcha_token: ['Вы закрыли капчу. Повторите попытку.']};
                    try {
                        window.smartCaptcha.reset(widgetId);
                    } catch (e) {
                    }
                }
            });

            setTimeout(() => {
                try {
                    window.smartCaptcha.execute(widgetId);
                } catch (e) {
                    this.loadings.sending = false;
                    this.form_errors = {captcha_token: ['Не удалось запустить капчу. Попробуйте снова.']};
                }
            }, 100);
        },

        setVehicle(id) {
            this.form.vehicle = id;
        },

        _normalizeSpecs(raw) {
            const toNum = (v) => Number(String(v ?? 0));
            const toBool = (v) => Number(v) === 1 || v === true;
            return (Array.isArray(raw) ? raw : []).map(s => ({
                type: String(s.type ?? '').toUpperCase(), // 'GOST' | 'TU' | и т.д.
                price: toNum(s.price),
                price_05: toNum(s.price_05),
                price_10: toNum(s.price_10),
                price_15: toNum(s.price_15),
                price_20: toNum(s.price_20),
                price_25: toNum(s.price_25),
                fiber_polypropylene: toBool(s.fiber_polypropylene),
                fiber_steel: toBool(s.fiber_steel),
            }));
        },
    }
});
